import { memo, ReactElement, useCallback, ChangeEvent } from 'react';
import { Input, Button, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined, CloseOutlined } from '@ant-design/icons';
import type { JobNumberItem } from '../types';
import { JOB_NUMBER_MAX_LENGTH } from '../constants';
import { JobNumberRowGrid, RowActions } from './styles';

interface Props {
  item: JobNumberItem;
  canCommit: boolean;
  /** この行のリクエスト送信中フラグ。送信中は入力・取り消しを無効化し、打鍵の喪失と二重操作を防ぐ。 */
  pending: boolean;
  onChangeJobNumber: (id: string, value: string) => void;
  onCommit: (id: string) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
}

const JobNumberRow = memo(function JobNumberRow({
  item,
  canCommit,
  pending,
  onChangeJobNumber,
  onCommit,
  onRemove,
}: Props): ReactElement {
  const { id, jobNumber, status } = item;
  const isEditing = status === 'editing';

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChangeJobNumber(id, e.target.value);
    },
    [id, onChangeJobNumber]
  );

  const handleCommit = useCallback(() => onCommit(id), [id, onCommit]);

  // Popconfirm の onConfirm に Promise を返すと、完了まで OK ボタンがローディング表示になる。
  // 戻り値型を明示し、ブロック本体への書き換えで Promise が返らなくなる退行を型エラーで検出する。
  const handleRemove = useCallback((): Promise<void> => onRemove(id), [id, onRemove]);

  return (
    <JobNumberRowGrid>
      <Input
        value={jobNumber}
        placeholder='仕事Noを入力'
        onChange={handleChange}
        disabled={!isEditing || pending}
        maxLength={JOB_NUMBER_MAX_LENGTH}
        aria-label='仕事No'
      />
      <RowActions>
        {isEditing ? (
          <>
            <Button
              size='small'
              type='primary'
              icon={<PlusOutlined />}
              onClick={handleCommit}
              disabled={!canCommit}
              aria-label='この行を追加'
            >
              追加
            </Button>
            <Button
              size='small'
              icon={<CloseOutlined />}
              onClick={handleRemove}
              disabled={pending}
              aria-label='この行を取り消し'
            >
              取り消し
            </Button>
          </>
        ) : (
          <Popconfirm
            title='この行を削除しますか？'
            description={`仕事No: ${jobNumber}`}
            onConfirm={handleRemove}
            okText='削除'
            cancelText='キャンセル'
            okButtonProps={{ danger: true }}
          >
            <Button size='small' danger icon={<DeleteOutlined />} aria-label='この行を削除'>
              削除
            </Button>
          </Popconfirm>
        )}
      </RowActions>
    </JobNumberRowGrid>
  );
});

export default JobNumberRow;
